<template>
  <div id="app" class="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 relative">
    <!-- 배경 장식 요소 -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
      <div class="absolute top-20 left-10 w-72 h-72 bg-blue-300/20 rounded-full blur-3xl animate-float"></div>
      <div class="absolute bottom-20 right-10 w-96 h-96 bg-purple-300/20 rounded-full blur-3xl animate-float" style="animation-delay: 1s;"></div>
      <div class="absolute top-1/2 left-1/3 w-64 h-64 bg-pink-300/15 rounded-full blur-3xl animate-float" style="animation-delay: 2s;"></div>
    </div>

    <!-- 헤더 네비게이션 -->
    <header class="glass-card m-4 fixed top-4 left-0 right-0 z-50 animate-slide-down" style="max-width: calc(100% - 2rem); margin-left: auto; margin-right: auto;">
      <nav class="container mx-auto px-6 py-4">
        <div class="flex items-center justify-between">
          <!-- 로고 -->
          <div class="flex items-center space-x-3 group cursor-pointer">
            <div class="w-12 h-12 bg-gradient-primary rounded-2xl flex items-center justify-center shadow-lg transform transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
              <span class="text-white font-bold text-2xl">🎯</span>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-gradient">Lotto Genius</h1>
              <p class="text-sm text-gray-500 font-medium">재미있는 로또 번호 선택 체험</p>
            </div>
          </div>

          <!-- 네비게이션 메뉴 -->
          <div class="hidden md:flex items-center gap-2 bg-gray-100/50 p-1.5 rounded-xl backdrop-blur-sm">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              :class="[
                'px-5 py-2.5 rounded-lg font-semibold transition-all duration-200',
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'text-gray-600 hover:bg-white/80 hover:text-blue-600',
              ]"
            >
              <span class="flex items-center gap-2">
                <span>{{ tab.icon }}</span>
                <span>{{ tab.name }}</span>
              </span>
            </button>
          </div>

          <!-- 모바일 메뉴 버튼 -->
          <button
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="md:hidden p-2.5 rounded-xl hover:bg-white/50 transition-all duration-300 backdrop-blur-sm"
          >
            <svg class="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                :d="mobileMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'">
              </path>
            </svg>
          </button>
        </div>

        <!-- 모바일 메뉴 -->
        <transition
          enter-active-class="transition duration-150"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="transition duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div v-if="mobileMenuOpen" class="md:hidden mt-4 pt-4 border-t border-gray-200/60">
            <div class="space-y-2">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                @click="activeTab = tab.id; mobileMenuOpen = false;"
                :class="[
                  'w-full text-left px-4 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-3',
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-white/50 hover:text-blue-600',
                ]"
              >
                <span class="text-xl">{{ tab.icon }}</span>
                <span>{{ tab.name }}</span>
              </button>
            </div>
          </div>
        </transition>
      </nav>
    </header>

    <!-- 메인 콘텐츠 with 사이드바 광고 -->
    <div class="container mx-auto px-4 pb-8 pt-32 relative z-10">
      <div class="flex gap-6">
        <!-- 왼쪽 사이드바 광고 (데스크톱 전용) -->
        <aside class="hidden xl:block w-[160px] flex-shrink-0">
          <div class="sticky top-28">
            <AdSenseUnit
              ad-slot="2696332240"
              ad-format="auto"
              ad-style="display:block"
              full-width-responsive="true"
            />
          </div>
        </aside>

        <!-- 메인 콘텐츠 -->
        <main class="flex-1 min-w-0">
          <transition
            mode="out-in"
            enter-active-class="transition duration-150"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
        <!-- 대시보드 탭 -->
        <div v-if="activeTab === 'dashboard'" key="dashboard">
          <Dashboard />
        </div>

        <!-- 재미 선택 탭 -->
        <div v-else-if="activeTab === 'generator'" key="generator">
          <FunNumberGenerator />
        </div>

        <!-- 히스토리 탭 -->
        <div v-else-if="activeTab === 'history'" key="history">
          <History />
        </div>

        <!-- 가이드 탭 -->
        <div v-else-if="activeTab === 'guide'" key="guide">
          <EducationHub @navigate="handleNavigate" />
        </div>

        <!-- FAQ 탭 -->
        <div v-else-if="activeTab === 'faq'" key="faq">
          <FAQ @navigate="handleNavigate" />
        </div>

        <!-- 개인정보처리방침 -->
        <div v-else-if="activeTab === 'privacy'" key="privacy">
          <PrivacyPolicy />
        </div>

        <!-- 이용약관 -->
        <div v-else-if="activeTab === 'terms'" key="terms">
          <TermsOfService />
        </div>

        <!-- 회사소개 -->
        <div v-else-if="activeTab === 'about'" key="about">
          <AboutUs />
        </div>

        <!-- 연락처 -->
        <div v-else-if="activeTab === 'contact'" key="contact">
          <Contact />
        </div>
          </transition>
        </main>

        <!-- 오른쪽 사이드바 광고 (데스크톱 전용) -->
        <aside class="hidden xl:block w-[160px] flex-shrink-0">
          <div class="sticky top-28">
            <AdSenseUnit
              ad-slot="5984840567"
              ad-format="auto"
              ad-style="display:block"
              full-width-responsive="true"
            />
          </div>
        </aside>
      </div>
    </div>

    <!-- 푸터 -->
    <footer class="glass-card m-4 mt-8 relative z-10">
      <div class="container mx-auto px-6 py-8">
        <!-- 로고 및 소개 -->
        <div class="text-center mb-6">
          <div class="flex items-center justify-center gap-2 mb-3">
            <div class="w-10 h-10 bg-gradient-primary rounded-lg flex items-center justify-center">
              <span class="text-white text-xl">🎯</span>
            </div>
            <p class="text-gray-700 font-bold text-lg">Lotto Genius</p>
          </div>
          <p class="text-sm text-gray-500 mb-4">재미있는 로또 번호 선택 체험 플랫폼</p>
          <div class="inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <span class="text-xl">🎪</span>
            <p class="text-sm text-gray-600">
              이 도구는 순전히 <strong class="text-yellow-700">재미</strong>를 위한 것입니다. 
              모든 번호의 당첨 확률은 <strong class="text-blue-600">동일</strong>합니다.
            </p>
          </div>
        </div>

        <!-- 링크 섹션 -->
        <div class="grid md:grid-cols-4 gap-6 mb-6 text-left">
          <!-- 서비스 -->
          <div>
            <h3 class="font-bold text-gray-700 mb-3">서비스</h3>
            <ul class="space-y-2 text-sm">
              <li>
                <button @click="activeTab = 'dashboard'" class="text-gray-600 hover:text-blue-600 transition-colors">
                  📊 대시보드
                </button>
              </li>
              <li>
                <button @click="activeTab = 'generator'" class="text-gray-600 hover:text-blue-600 transition-colors">
                  🎨 번호 생성
                </button>
              </li>
              <li>
                <button @click="activeTab = 'history'" class="text-gray-600 hover:text-blue-600 transition-colors">
                  📜 히스토리
                </button>
              </li>
            </ul>
          </div>

          <!-- 가이드 -->
          <div>
            <h3 class="font-bold text-gray-700 mb-3">가이드</h3>
            <ul class="space-y-2 text-sm">
              <li>
                <button @click="activeTab = 'guide'" class="text-gray-600 hover:text-blue-600 transition-colors">
                  📚 로또 가이드
                </button>
              </li>
              <li>
                <button @click="activeTab = 'faq'" class="text-gray-600 hover:text-blue-600 transition-colors">
                  ❓ 자주 묻는 질문
                </button>
              </li>
            </ul>
          </div>

          <!-- 정보 -->
          <div>
            <h3 class="font-bold text-gray-700 mb-3">정보</h3>
            <ul class="space-y-2 text-sm">
              <li>
                <button @click="activeTab = 'about'" class="text-gray-600 hover:text-blue-600 transition-colors">
                  🎯 서비스 소개
                </button>
              </li>
              <li>
                <button @click="activeTab = 'contact'" class="text-gray-600 hover:text-blue-600 transition-colors">
                  📧 문의하기
                </button>
              </li>
            </ul>
          </div>

          <!-- 정책 -->
          <div>
            <h3 class="font-bold text-gray-700 mb-3">정책</h3>
            <ul class="space-y-2 text-sm">
              <li>
                <button @click="activeTab = 'privacy'" class="text-gray-600 hover:text-blue-600 transition-colors">
                  🔒 개인정보처리방침
                </button>
              </li>
              <li>
                <button @click="activeTab = 'terms'" class="text-gray-600 hover:text-blue-600 transition-colors">
                  📜 이용약관
                </button>
              </li>
            </ul>
          </div>
        </div>

        <!-- 구분선 -->
        <div class="border-t border-gray-200 pt-6">
          <div class="text-center text-sm text-gray-500">
            <p>© 2025 Lotto Genius. All rights reserved.</p>
            <p class="mt-2">Made with ❤️ in Korea</p>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";

// 컴포넌트 임포트
import Dashboard from "./components/Dashboard.vue";
import FunNumberGenerator from "./components/FunNumberGenerator.vue";
import History from "./components/History.vue";
import EducationHub from "./components/guides/EducationHub.vue";
import FAQ from "./components/FAQ.vue";
import PrivacyPolicy from "./components/PrivacyPolicy.vue";
import TermsOfService from "./components/TermsOfService.vue";
import AboutUs from "./components/AboutUs.vue";
import Contact from "./components/Contact.vue";
import AdSenseUnit from "./components/AdSenseUnit.vue";

// 반응형 상태 정의
const activeTab = ref("dashboard");
const mobileMenuOpen = ref(false);

// 탭 구성
const tabs = [
  { id: "dashboard", name: "대시보드", icon: "📊" },
  { id: "generator", name: "번호 생성", icon: "🎨" },
  { id: "history", name: "히스토리", icon: "📜" },
  { id: "guide", name: "가이드", icon: "📚" },
  { id: "faq", name: "FAQ", icon: "❓" },
];

// 네비게이션 핸들러
const handleNavigate = (page) => {
  activeTab.value = page;
  mobileMenuOpen.value = false;
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// 생명주기 훅
onMounted(() => {
  console.log("🎯 Lotto Genius 앱이 시작되었습니다!");
});
</script>
